import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import authService from '../../services/authService';
import FormField from '../common/FormField';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import './MfaSetup.css';

const AppMethod = ({ onComplete, setError }) => {
  const [setupData, setSetupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    authService.setupMfa()
      .then(setSetupData)
      .catch(() => setError('Failed to initialize MFA setup. Please try again later.'))
      .finally(() => setLoading(false));
  }, [setError]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');
    try {
      await authService.verifyMfa(code, { secret: setupData?.secret, channel: 'app' });
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <div className="mfa-setup__steps">
        <div className="mfa-step">
          <span className="mfa-step__num">1</span>
          <p>Install an authenticator app like Google Authenticator or Authy on your mobile device.</p>
        </div>

        <div className="mfa-step">
          <span className="mfa-step__num">2</span>
          <p>Scan this QR code with the app, or enter the secret key manually.</p>
        </div>

        {setupData && (
          <div className="mfa-setup__qr-container">
            <div className="qr-placeholder">
              <QRCodeSVG value={setupData.qr_code_url} size={150} title="MFA QR Code" />
            </div>
            <p className="mfa-setup__secret">Secret Key: <strong>{setupData.secret}</strong></p>
          </div>
        )}

        <div className="mfa-step">
          <span className="mfa-step__num">3</span>
          <p>Enter the 6-digit code generated by your app to verify setup.</p>
        </div>
      </div>

      <form onSubmit={handleVerify} className="mfa-setup__form">
        <FormField
          label="Verification Code"
          name="code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          required
        />
        <button type="submit" className="mfa-setup__submit" disabled={verifying || code.length !== 6}>
          {verifying ? 'Verifying...' : 'Verify and Enable'}
        </button>
      </form>
    </>
  );
};

const EmailMethod = ({ onComplete, setError }) => {
  const [sending, setSending] = useState(true);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [notice, setNotice] = useState('');

  const sendCode = async () => {
    setSending(true);
    setError('');
    try {
      await authService.requestMfaEmailCode();
      setNotice('A verification code has been emailed to you.');
    } catch (err) {
      setError(err.response?.data?.message || 'The code could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => { sendCode(); }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');
    try {
      await authService.verifyMfa(code, { channel: 'email' });
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <div className="mfa-setup__steps">
        <div className="mfa-step">
          <span className="mfa-step__num">1</span>
          <p>We email a 6-digit code to your account's address each time you sign in.</p>
        </div>
        <div className="mfa-step">
          <span className="mfa-step__num">2</span>
          <p>Enter the code we just sent to verify setup.</p>
        </div>
      </div>

      {notice && <Alert type="success" message={notice} />}

      <form onSubmit={handleVerify} className="mfa-setup__form">
        <FormField
          label="Verification Code"
          name="code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          required
        />
        <div className="mfa-setup__actions">
          <button type="button" className="btn-secondary" onClick={sendCode} disabled={sending}>
            {sending ? 'Sending…' : 'Resend code'}
          </button>
          <button type="submit" className="mfa-setup__submit" disabled={verifying || code.length !== 6}>
            {verifying ? 'Verifying...' : 'Verify and Enable'}
          </button>
        </div>
      </form>
    </>
  );
};

const MfaSetup = ({ onComplete }) => {
  const [method, setMethod] = useState(null); // 'app' | 'email'
  const [error, setError] = useState('');

  return (
    <div className="mfa-setup">
      <h3 className="mfa-setup__title">Set up Two-Factor Authentication</h3>

      {error && <Alert type="error" message={error} />}

      {!method ? (
        <div className="mfa-setup__method-choice">
          <p>Choose how you'd like to verify it's you when signing in.</p>
          <button type="button" className="btn-secondary" onClick={() => setMethod('app')}>
            Use an authenticator app
          </button>
          <button type="button" className="btn-secondary" onClick={() => setMethod('email')}>
            Email me a code instead
          </button>
        </div>
      ) : method === 'app' ? (
        <AppMethod onComplete={onComplete} setError={setError} />
      ) : (
        <EmailMethod onComplete={onComplete} setError={setError} />
      )}
    </div>
  );
};

export default MfaSetup;
